import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject } from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-app/shared/fi-items-list/fi-remote-state-full-data-source';
import { IStatistic2ReportParameter } from '../statistic2';
import { ObjectTypes } from '@fi-app/shared/types';
import { FiItemsListType } from '@fi-app/shared/fi-items-list/fi-items-list-type';
import {
    IItemsListAction2,
    IItemsListDataFetchResult,
    IItemsListParameterBase,
} from '@fi-app/shared/fi-items-list/fi-items-list';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { ILoadDataArgs } from '@fi-app/shared/fi-items-list/fi-data-source-base.directive';
import { Statistic2Service } from '../statistic2.service';
import { Statistic2HelperService } from '../statistic2-helper.service';
import { Statistic2ReportType } from '../statistic2-types';
import { Statistic2ReportParameterEditDialogComponent } from '../statistic2-report-parameter-edit/statistic2-report-parameter-edit-dialog/statistic2-report-parameter-edit-dialog.component';
import { cloneDeep } from '@fi-app/shared/utils/clone-deep';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';

@Component({
    selector: 'fi-statistic2-report-list',
    templateUrl: './statistic2-report-list.component.html',
    styleUrls: ['./statistic2-report-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-statistic2-report-list' },
    standalone: false,
})
export class Statistic2ReportListComponent extends FiRemoteStateFullDataSource<IStatistic2ReportParameter> {
    public defaultSortColumnName: string = 'Name';
    public defaultSortDirection: boolean = false;
    public objectType: ObjectTypes = ObjectTypes.Statistic2;
    public itemsListType = FiItemsListType.Statistics; // ? change to Statistic2Reports

    private statistic2Service = inject(Statistic2Service);
    private statistic2HelperService = inject(Statistic2HelperService);
    private loadingService = inject(LoadingService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);

    protected itemsListActions: IItemsListAction2[] = [
        {
            title: this.appTextService.get('common.delete'),
            onClick: () => this.confirmDelteSelectedItems(),
        },
    ];
    protected listItemActions: IItemsListAction2[] = [
        {
            title: this.appTextService.get('statistic.edit_report'),
            onClick: (reportParameter: IStatistic2ReportParameter) => {
                this.openEditReportDialog(reportParameter);
            },
        },
    ];

    private confirmDelteSelectedItems(): void {
        this.fiItemsDeleteHelperService.commonDeleteConfirm(ObjectTypes.Statistic2, this.selectedItems()).then(() => {
            this.deleteSelectedItems();
        });
    }

    private deleteSelectedItems(): void {
        const items = this.selectedItems();
        this.statistic2Service.deleteReportParameters(items).then(() => this.reloadData());
    }

    public loadListDataFn(
        loadArgs: ILoadDataArgs,
    ): IFiHttpPromise<IItemsListDataFetchResult<IStatistic2ReportParameter>> {
        const parameter: IItemsListParameterBase = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        return this.statistic2Service.getReportParametersListData(parameter);
    }

    protected openExtendedReportView($event: MouseEvent, reportParameter: IStatistic2ReportParameter): void {
        $event.stopPropagation();
        this.statistic2HelperService.openExtendedReportView(reportParameter);
    }

    protected getReportTypeText(reportType: Statistic2ReportType): string {
        return this.statistic2HelperService.getReportTypeText(reportType);
    }

    protected getReadRatingForText(reportParameter: IStatistic2ReportParameter): string {
        return this.statistic2HelperService.getReportForText(reportParameter);
    }

    protected getReportPeriodText(reportParameter: IStatistic2ReportParameter): string {
        return this.statistic2HelperService.getReportPeriodText(reportParameter);
    }

    protected getResultsShownText(reportParameter: IStatistic2ReportParameter): string {
        return (
            this.appTextService.get('statistic.per') +
            ' ' +
            this.statistic2HelperService.getShowResultsPerText(reportParameter.ShowResultsPerType).toLowerCase()
        );
    }

    private openEditReportDialog(reportParameter: IStatistic2ReportParameter): void {
        this.statistic2HelperService
            .showEditReportDialog(
                'statistic.edit_statistics_report',
                Statistic2ReportParameterEditDialogComponent,
                cloneDeep(reportParameter),
            )
            .onYesButtonClick((editedReportParameter) => {
                editedReportParameter.DataRepresentationTypeView = editedReportParameter.DataRepresentationType;
                this.loadingService.trackPromise(this.statistic2Service.saveReportParameter(editedReportParameter));
                this.updateItems((items) => {
                    return items.map((item) => {
                        if (item.Id === editedReportParameter.Id) {
                            return editedReportParameter;
                        }
                        return item;
                    });
                });
            });
    }
}
